import styled, { css } from 'styled-components/macro';

import { ColorVariants } from 'theme';

export const FormTextStyles = css`
    font-size: 0.875rem;
    display: block;
    outline: none;
    width: 100%;
    color: ${({ theme }) => theme.colors.semidarkGrey};

    padding: 10px ${({ theme }) => theme.space.base};
    -webkit-appearance: none;
    background-color: transparent;

    border: 2px solid ${({ theme }) => theme.colors.labels};
    border-radius: 60px;
    box-sizing: border-box;

    &::placeholder {
        color: ${({ theme }) => theme.colors.inactive};
    }
`;

export const FormText = styled.input`
    ${FormTextStyles}
`;

export const FormPasswordText = styled.input`
    ${FormTextStyles}

    border: 0;
    padding-right: 0;
`;

export const FormPasswordIcon = styled.button`
    ${FormTextStyles}

    border: 0;
    width: 60px;
    padding: 0;

    span {
        vertical-align: middle;
        color: ${({ theme }) => theme.colors.inactive};

        &:hover {
            color: ${({ theme }) => theme.colors.body};
        }
    }
`;

export const FormPasswordFieldStyles = styled.div`
    ${FormTextStyles}

    padding: 0;
    display: flex;
`;

export const RadioLabel = styled.label`
    position: relative;
    font-size: 1rem;
    line-height: 1.5rem;

    padding-left: 32px;
    cursor: pointer;
    display: inline-block;
    min-height: 22px;
    margin-left: ${({ theme }) => theme.space.medium};

    &:before {
        display: block;
        position: absolute;
        content: '';
        top: 2px;
        left: 0px;
        width: 18px;
        height: 18px;
        pointer-events: none;
        transition: all 150ms linear;
        border: 1px solid ${({ theme }) => theme.colors.inactive};
        border-radius: 100%;
    }

    &:after {
        position: absolute;
        content: '';
        top: 6px;
        left: 4px;
        width: 12px;
        height: 12px;
        opacity: 0;
        border-radius: 100%;
        transition: all 150ms linear;
        background-color: ${({ theme }) => theme.colors.body};
    }
`;

export const Radio = styled.input<{ $color?: ColorVariants }>`
    position: absolute;
    z-index: -1;
    opacity: 0;

    &:checked + ${RadioLabel} {
        &:before {
            border-color: ${({ theme, $color }) =>
                $color ? theme.colors[$color] : theme.colors.brand};
        }

        &:after {
            opacity: 1;
            background-color: ${({ theme, $color }) =>
                $color ? theme.colors[$color] : theme.colors.brand};
        }
    }
`;

export const RadioGroup = styled.div<{
    orientation: 'horizontal' | 'vertical';
    align: 'left' | 'center';
}>`
    display: flex;

    ${({ align }) =>
        align === 'center' &&
        css`
            justify-content: center;
        `}

    ${({ orientation, theme }) =>
        orientation === 'vertical' &&
        css`
            flex-direction: column;

            .RadioItem_sc {
                &:not(:first-child) {
                    margin-top: ${theme.space.base};
                }
            }
        `}
`;

export const FormLabel = styled.label`
    display: block;
    margin-bottom: ${({ theme }) => theme.space.tiny};
    font-size: 0.875rem;
    line-height: 1.125rem;
    color: ${({ theme }) => theme.colors.baseGray};

    &[for]:not([for='']) {
        cursor: pointer;
    }
`;

export const FormTextareaStyles = css`
    outline: none;
    width: 100%;
    padding: ${({ theme }) => theme.space.base};
    -webkit-appearance: none;

    background-color: transparent;
    border: 0;
    height: auto;

    color: ${({ theme }) => theme.colors.semidarkGrey};
    border: 2px solid ${({ theme }) => theme.colors.labels};
    border-radius: 10px;
    box-sizing: border-box;

    overflow: auto;
    min-height: 60px;
    resize: vertical;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &::placeholder {
        color: ${({ theme }) => theme.colors.inactive};
    }
`;

export const FormTextarea = styled.textarea`
    ${FormTextareaStyles};
`;

export const FormItem = styled.div`
    position: relative;
`;

export const FormField = styled.div<{
    isError: boolean;
    isRadio: boolean;
}>`
    width: 100%;

    &:not(:first-child) {
        margin-top: ${({ theme }) => theme.space.medium};
    }

    ${({ isRadio, theme }) =>
        isRadio
            ? `
                ${FormLabel} {
                    margin-bottom: ${theme.space.small};
                }

                ${FormItem} {
                    margin-left: -${theme.space.medium};
                }
            `
            : ''}

    ${({ isError, theme }) =>
        isError
            ? `
                ${FormLabel} {
                    color: ${theme.colors.error};
                }

                ${FormText} {
                    border-color: ${theme.colors.error};
                }

                ${FormTextarea} {
                    border-color: ${theme.colors.error};
                }
            `
            : ''}

    .maskedInputStyles {
        ${FormTextStyles}

        ${({ isError, theme }) =>
            isError &&
            css`
                border-color: ${theme.colors.error};
            `}
    }
`;

export const FormError = styled.div`
    margin-top: ${({ theme }) => theme.space.tiny};
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: ${({ theme }) => theme.colors.error};
`;

export const FormCheckLabel = styled.label`
    position: relative;
    font-size: 1rem;
    line-height: 1.5rem;
    padding-left: 34px;
    cursor: pointer;
    display: inline-block;
    min-height: 22px;

    &:before {
        display: block;
        position: absolute;
        top: 0;
        left: 0px;
        width: 18px;
        height: 18px;
        pointer-events: none;
        margin-right: ${({ theme }) => theme.space.base};
        content: '';
        border-radius: 2px;
        transition: all 150ms linear;
        border: 1px solid ${({ theme }) => theme.colors.inactive};
        flex-shrink: 0;
    }

    &:after {
        position: absolute;
        top: 1px;
        left: 1px;
        content: '';
        width: 18px;
        height: 18px;
        opacity: 0;
        transition: all 150ms linear;
        background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3e%3cpath fill='%23ffffff' d='M29.439 5.439c0.586-0.586 1.536-0.586 2.121 0s0.586 1.536 0 2.121l-18.5 18.5c-0.586 0.586-1.536 0.586-2.121 0l-10.5-10.5c-0.586-0.586-0.586-1.536 0-2.121s1.536-0.586 2.121 0l9.439 9.439 17.439-17.439z'/%3e%3c/svg%3e")
            no-repeat right 2px center/14px 15px;
    }

    a {
        cursor: pointer;
        font-weight: 500;
        text-decoration: underline;
        &:hover {
            color: ${({ theme }) => theme.colors.brand};
        }
    }
`;

export const FormCheck = styled.input`
    position: absolute;
    z-index: -1;
    opacity: 0;

    &:checked + ${FormCheckLabel} {
        &:before {
            background-color: ${({ theme }) => theme.colors.body};
            border-color: ${({ theme }) => theme.colors.body};
        }

        &:after {
            opacity: 1;
        }
    }
`;

export const FormDropdown = styled.select`
    display: inline-block;
    width: 100%;
    padding: 0 ${({ theme }) => theme.space.base};
    vertical-align: middle;
    height: 42px;
    line-height: 42px;
    border-radius: 0;
    background-color: transparent;
    border: 1px solid ${({ theme }) => theme.colors.labels};
    appearance: none;
    word-wrap: normal;
    box-sizing: border-box;
    background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3e%3cpath fill='%23939393' d='M16 20.358l-12.766-12.764c-0.342-0.366-0.827-0.594-1.366-0.594-1.031 0-1.867 0.836-1.867 1.868 0 0.539 0.228 1.025 0.593 1.365l14.087 14.087c0.338 0.337 0.804 0.546 1.32 0.546s0.982-0.209 1.32-0.546l14.086-14.086c0.366-0.342 0.594-0.827 0.594-1.366 0-1.031-0.836-1.867-1.867-1.867-0.539 0-1.025 0.228-1.365 0.593l-12.767 12.765z'/%3e%3c/svg%3e")
        no-repeat right 14px center/14px 15px;

    &:focus {
        border-color: ${({ theme }) => theme.colors.body};
        outline: none;
    }
`;
